// ============================================================================
// Componente: FinanceiroAutomacaoImpostosFederais.js
// Objetivo: gerar o CALCULO IMPOSTOS FEDERAIS do mês a partir da planilha de folha.
// Padrão visual: Financeiro / Verde-azulado
//
// São DUAS entradas, e as duas são obrigatórias — daí a tela ter duas áreas de
// upload em vez de uma. A segunda não é acessório: é dela que sai o PIS/COFINS por
// loja, que não existe na folha (a folha só traz o consolidado da empresa).
//
// ⚠️ A segunda área aceita VÁRIOS arquivos porque a apuração fiscal sai um arquivo
// POR EMPRESA (4 arquivos). Quem decide o que é cada um é o Python, pelo conteúdo.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import {
    AlertCircle,
    AlertTriangle,
    CheckCircle2,
    Download,
    Eraser,
    FileSpreadsheet,
    FileText,
    Loader2,
    UploadCloud
} from 'lucide-react';

const AutomacaoImpostosFederais = () => {
    const corModulo = '#16a39c';
    const corModuloEscura = '#0f766e';
    const corFundoClaro = '#f0fdfa';
    const corBorda = '#99f6e4';

    const inputFolhaRef = useRef(null);
    const inputReferenciaRef = useRef(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [arquivoFolha, setArquivoFolha] = useState(null);
    // Lista, não arquivo único: a apuração fiscal sai um arquivo por empresa.
    const [arquivosReferencia, setArquivosReferencia] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [mensagem, setMensagem] = useState(null);

    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('Aguardando arquivos...');
    const [isPolling, setIsPolling] = useState(false);
    const [executionId, setExecutionId] = useState(null);
    // Os avisos do script (diferença a alocar, PIS/COFINS ausente) não são erro —
    // são decisões do Financeiro. Ficam guardados para aparecer no fim, junto com o
    // aviso de conferir a aba CONFERENCIA.
    const [avisos, setAvisos] = useState([]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        let interval;

        if (isPolling && executionId) {
            interval = setInterval(async () => {
                try {
                    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
                    const res = await fetch(`/api/automacoes-financeiro/logs/${executionId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (!res.ok) return;

                    const data = await res.json();
                    if (data.logs && data.logs.length > 0) {
                        const ultimo = String(data.logs[data.logs.length - 1]).trim();
                        setStatusText(ultimo);

                        if (ultimo.includes('Competencia')) setProgress(25);
                        else if (ultimo.includes('estabelecimentos')) setProgress(55);
                        else if (ultimo.includes('A ALOCAR')) setProgress(75);
                        else if (ultimo.includes('Gerado')) setProgress(100);

                        // As linhas de aviso do script vêm marcadas com '·'.
                        const marcados = data.logs
                            .map((l) => String(l).trim())
                            .filter((l) => l.startsWith('·'))
                            .map((l) => l.replace(/^·\s*/, ''));
                        if (marcados.length) setAvisos(marcados);
                    }
                } catch (_) {}
            }, 1000);
        }

        return () => clearInterval(interval);
    }, [isPolling, executionId]);

    const handleLimpar = () => {
        setArquivoFolha(null);
        setArquivosReferencia([]);
        setMensagem(null);
        setAvisos([]);
        setProgress(0);
        setStatusText('Aguardando arquivos...');
        setExecutionId(null);
        setIsPolling(false);

        if (inputFolhaRef.current) inputFolhaRef.current.value = '';
        if (inputReferenciaRef.current) inputReferenciaRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!arquivoFolha || arquivosReferencia.length === 0) {
            setMensagem({ type: 'error', text: 'Envie a planilha de folha e ao menos um arquivo de PIS/COFINS do mês.' });
            return;
        }

        setCarregando(true);
        setMensagem(null);
        setAvisos([]);
        setProgress(5);
        setStatusText('Enviando arquivos...');

        const novoExecutionId = `impostos_${Date.now()}`;
        setExecutionId(novoExecutionId);
        setIsPolling(true);

        const formData = new FormData();
        formData.append('executionId', novoExecutionId);
        formData.append('folha', arquivoFolha);
        // Mesmo nome de campo repetido: é assim que o multer monta o array no backend.
        arquivosReferencia.forEach((arquivo) => formData.append('referencia', arquivo));

        try {
            const token = localStorage.getItem('token') || localStorage.getItem('authToken');
            const response = await fetch('/api/automacoes-financeiro/calculo-impostos-federais', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });

            if (!response.ok) {
                let erro = {};
                try {
                    erro = await response.json();
                } catch (_) {}
                // O motivo técnico (o que o script reclamou) vai para o quadro de
                // avisos: sem isso, quem vê a falha vê só "erro ao gerar" e não tem
                // como agir nem relatar — foi o que aconteceu no primeiro uso.
                if (Array.isArray(erro.detalhe) && erro.detalhe.length) setAvisos(erro.detalhe);
                throw new Error(erro.error || 'Erro ao gerar o cálculo.');
            }

            const blob = await response.blob();
            const contentDisposition = response.headers.get('content-disposition');
            let nomeArquivo = 'CALCULO IMPOSTOS FEDERAIS.xlsx';
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?(.*?)"?(;|$)/);
                if (match?.[1]) nomeArquivo = match[1].replace(/['"]/g, '');
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nomeArquivo);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setProgress(100);
            setStatusText('Arquivo baixado.');
            setMensagem({
                type: 'success',
                text: 'Cálculo gerado. Confira a aba CONFERENCIA antes de lançar no SAP.'
            });
        } catch (error) {
            setMensagem({ type: 'error', text: error.message || 'Erro de conexão com o servidor.' });
            setStatusText('Falha na geração.');
        } finally {
            setCarregando(false);
            setTimeout(() => setIsPolling(false), 3000);
        }
    };

    const corDaBarra = () => {
        if (mensagem?.type === 'error') return '#ef4444';
        if (mensagem?.type === 'success') return '#10b981';
        return corModulo;
    };

    const cardStyle = {
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: isMobile ? '20px' : '28px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)'
    };

    // `arquivos` é sempre uma LISTA, mesmo na área de um arquivo só — assim a área
    // de upload tem um formato único e a de vários não precisa de um segundo layout.
    const AreaDeUpload = ({ inputRef, arquivos, aoTrocar, aceita, titulo, descricao, Icone, multiplos }) => (
        <div style={{ flex: 1, minWidth: isMobile ? '100%' : '280px' }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: '800', fontSize: '13px', color: '#334155' }}>{titulo}</p>
            <div style={{
                border: `2px dashed ${corBorda}`,
                borderRadius: '14px',
                padding: '28px 16px',
                textAlign: 'center',
                backgroundColor: '#f8fafc',
                cursor: carregando ? 'not-allowed' : 'pointer',
                position: 'relative',
                minHeight: '150px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <input
                    ref={inputRef}
                    type="file"
                    accept={aceita}
                    multiple={!!multiplos}
                    onChange={aoTrocar}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: carregando ? 'not-allowed' : 'pointer' }}
                    disabled={carregando}
                />
                {arquivos.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                        <div style={{ backgroundColor: '#f1f5f9', padding: '14px', borderRadius: '50%', color: '#94a3b8' }}>
                            <UploadCloud size={26} />
                        </div>
                        <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>{descricao}</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', width: '100%' }}>
                        <div style={{ backgroundColor: corFundoClaro, padding: '14px', borderRadius: '50%', color: corModulo }}>
                            <Icone size={26} />
                        </div>
                        {/* Todos os nomes ficam à vista: com 4 arquivos, "4 arquivos"
                            esconderia justamente a empresa que faltou selecionar. */}
                        <div style={{ width: '100%' }}>
                            {arquivos.map((arquivo) => (
                                <p key={arquivo.name} style={{ margin: '0 0 4px 0', fontWeight: '800', color: '#0f172a', fontSize: '13px', wordBreak: 'break-word' }}>
                                    {arquivo.name}
                                    <span style={{ fontWeight: '600', color: '#64748b' }}> · {(arquivo.size / 1024).toFixed(0)} KB</span>
                                </p>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={cardStyle}>
                <div style={{ backgroundColor: corFundoClaro, borderLeft: `4px solid ${corModulo}`, padding: '16px 20px', borderRadius: '10px', marginBottom: '24px' }}>
                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: corModuloEscura, fontSize: '15px', fontWeight: '800', margin: 0 }}>
                        <FileText size={18} /> Cálculo dos Impostos Federais
                    </h4>
                    <p style={{ color: '#115e59', fontSize: '14px', lineHeight: '1.6', margin: '6px 0 0 0' }}>
                        Transforma a planilha de folha (INSS, IRRF e FGTS) no cálculo do mês:
                        resumo por loja, blocos de lançamento para o SAP e fechamento do FGTS.
                        A competência é lida de dentro da planilha, não do nome do arquivo.
                        <br />
                        <strong>Por que a segunda área:</strong> o PIS/COFINS <strong>por loja</strong> não
                        existe na folha — ela só traz o consolidado de cada empresa. Envie ali os
                        arquivos <strong>do mês que está sendo gerado</strong>: o CALCULO com as abas
                        <em> PIS CONFINS</em> e/ou os arquivos da tela Fiscal › Apuração de Impostos
                        (um por empresa, por isso o campo aceita vários).
                        <br />
                        <strong>⚠️ Só a aba PIS CONFINS traz o valor a recolher</strong> (já com o
                        crédito de compras abatido). Se você enviar apenas os arquivos da apuração,
                        o cálculo sai pelo <strong>débito</strong> — acima do devido — e o aviso
                        aparece aqui embaixo.
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
                        <AreaDeUpload
                            inputRef={inputFolhaRef}
                            arquivos={arquivoFolha ? [arquivoFolha] : []}
                            aceita=".xls,.xlsx"
                            titulo="1. Planilha de folha do mês (a que o RH envia)"
                            descricao="Abas INSS, IRRF e FGTS (.xls)"
                            Icone={FileSpreadsheet}
                            aoTrocar={(e) => {
                                setArquivoFolha(e.target.files?.[0] || null);
                                setMensagem(null);
                                setProgress(0);
                                setStatusText('Aguardando arquivos...');
                            }}
                        />
                        <AreaDeUpload
                            inputRef={inputReferenciaRef}
                            arquivos={arquivosReferencia}
                            aceita=".xls,.xlsx"
                            multiplos
                            titulo="2. PIS/COFINS do mês (pode enviar vários)"
                            descricao="O CALCULO do mês e/ou os arquivos da Apuração de Impostos"
                            Icone={FileSpreadsheet}
                            aoTrocar={(e) => {
                                setArquivosReferencia(Array.from(e.target.files || []));
                                setMensagem(null);
                                setProgress(0);
                                setStatusText('Aguardando arquivos...');
                            }}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            onClick={handleLimpar}
                            disabled={carregando}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 18px', borderRadius: '8px', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '800', border: '1px solid #cbd5e1', cursor: carregando ? 'not-allowed' : 'pointer', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}
                        >
                            <Eraser size={18} /> Limpar
                        </button>

                        <button
                            type="submit"
                            disabled={carregando || !arquivoFolha || !arquivosReferencia.length}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '8px', backgroundColor: carregando || !arquivoFolha || !arquivosReferencia.length ? '#cbd5e1' : corModulo, color: '#fff', fontWeight: '800', border: 'none', cursor: carregando || !arquivoFolha || !arquivosReferencia.length ? 'not-allowed' : 'pointer', boxShadow: '0 4px 8px -3px rgba(22, 163, 156, 0.45)', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}
                        >
                            {carregando ? <><Loader2 size={18} className="animate-spin" /> Gerando...</> : <><Download size={18} /> Gerar Cálculo</>}
                        </button>
                    </div>
                </form>

                {mensagem && !carregando && (
                    <div style={{
                        backgroundColor: mensagem.type === 'success' ? '#ecfdf5' : '#fef2f2',
                        border: `1px solid ${mensagem.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
                        color: mensagem.type === 'success' ? '#047857' : '#b91c1c',
                        padding: '16px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginTop: '24px',
                        fontSize: '14px',
                        fontWeight: '700'
                    }}>
                        {mensagem.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                        {mensagem.text}
                    </div>
                )}

                {/* Os avisos não são falha: são o que o script apurou e não decide sozinho.
                    Ficam abaixo do resultado para não competir com ele. */}
                {avisos.length > 0 && !carregando && (
                    <div style={{
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: '12px',
                        padding: '16px',
                        marginTop: '16px'
                    }}>
                        <p style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 10px 0', fontWeight: '800', fontSize: '14px', color: '#92400e' }}>
                            <AlertTriangle size={18} />
                            {/* Na falha o mesmo quadro carrega o motivo — e um título
                                "confira antes de lançar" ali seria mentira: não há o
                                que lançar. */}
                            {mensagem?.type === 'error' ? 'O que o script reclamou' : 'Confira antes de lançar'}
                        </p>
                        <ul style={{ margin: 0, paddingLeft: '20px', color: '#78350f', fontSize: '13px', lineHeight: '1.7' }}>
                            {avisos.map((aviso, indice) => (
                                <li key={indice}>{aviso}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {(carregando || progress > 0) && (
                <div style={cardStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {carregando && <Loader2 size={24} color={corModulo} className="animate-spin" />}
                            {!carregando && mensagem?.type === 'success' && <CheckCircle2 size={24} color="#10b981" />}
                            {!carregando && mensagem?.type === 'error' && <AlertCircle size={24} color="#ef4444" />}
                            <div>
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>Status da Execução</h4>
                                <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>{statusText}</p>
                            </div>
                        </div>
                        <span style={{ fontSize: '18px', fontWeight: '900', color: corDaBarra() }}>{Math.round(progress)}%</span>
                    </div>

                    <div style={{ width: '100%', height: '10px', backgroundColor: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(progress, 100)}%`, height: '100%', backgroundColor: corDaBarra(), transition: 'width 0.4s ease' }} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutomacaoImpostosFederais;
